import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isTeamBudgetAlert, getGlobalThresholds } from '../utils/thresholds';
import { getAcknowledgedBlocks, acknowledgeBlock, clearAcknowledgedBlock } from '../services/api';

// Expandable stat card — clicking toggles an inline detail list
const ExpandableCard = ({ id, expanded, onToggle, className, children, detail }) => (
    <div
        className={`stat-card stat-card--expandable${expanded ? ' stat-card--expanded' : ''} ${className}`}
        onClick={() => onToggle(id)}
        role="button"
        tabIndex={0}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle(id)}
    >
        <div className="stat-card-expand-chevron">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <path d="m6 9 6 6 6-6" />
            </svg>
        </div>
        {children}
        {expanded && detail && (
            <div className="stat-card-detail">
                {detail}
            </div>
        )}
    </div>
);

const SystemHealth = ({ data, loading, loadingStatus, loadingMessage, onProvision, mcpServers = [], owuiTools = [], owuiSkills = [], isAdmin = false, onCheckModels }) => {
    const { t } = useTranslation();
    const [expandedCard, setExpandedCard] = useState(null);
    const [checkingModels, setCheckingModels] = useState(false);
    const [showRecentModal, setShowRecentModal] = useState(false);
    const [showUnassignedModal, setShowUnassignedModal] = useState(false);
    // Seed lastChecked from the persisted cache timestamp so it shows on page load
    const [lastChecked, setLastChecked] = useState(
        data.modelHealthCheckedAt ? new Date(data.modelHealthCheckedAt) : null
    );

    // { [teamId]: { teamAlias, acknowledgedBy, acknowledgedAt } }
    const [acknowledgedBlocks, setAcknowledgedBlocks] = useState({});

    useEffect(() => {
        getAcknowledgedBlocks().then(r => {
            if (r.success && r.data) setAcknowledgedBlocks(r.data);
        });
    }, []);

    const handleAcknowledge = useCallback(async (e, team) => {
        e.stopPropagation();
        const res = await acknowledgeBlock(team.team_id, team.team_alias);
        if (res.success) {
            setAcknowledgedBlocks(prev => ({
                ...prev,
                [team.team_id]: { teamAlias: team.team_alias, acknowledgedBy: res.data?.acknowledgedBy, acknowledgedAt: res.data?.acknowledgedAt },
            }));
        }
    }, []);

    const handleUnacknowledge = useCallback(async (e, teamId) => {
        e.stopPropagation();
        const res = await clearAcknowledgedBlock(teamId);
        if (res.success) {
            setAcknowledgedBlocks(prev => {
                const next = { ...prev };
                delete next[teamId];
                return next;
            });
        }
    }, []);

    const handleCheckModels = async (e) => {
        e.stopPropagation();
        if (checkingModels || !onCheckModels) return;
        setCheckingModels(true);
        try {
            await onCheckModels();
            setLastChecked(new Date());
        } finally {
            setCheckingModels(false);
        }
    };

    const toggleCard = (id) => setExpandedCard(prev => prev === id ? null : id);

    const formatRelativeTime = (iso) => {
        const diffMs = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        return `${hrs}h ago`;
    };

    const isLiteLLMReachable = data.litellmPing?.reachable !== false;
    const isOpenWebUIReachable = data.openWebUIPing?.reachable !== false;

    if (loading || loadingStatus?.overall === 'loading') {
        return (
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">{t('systemHealth.title')}</h2>
                </div>
                <div className="loading">{loadingMessage || t('systemHealth.loading')}</div>
            </div>
        );
    }

    if (!data.teams || data.teams.length === 0 || !data.models || !data.openWebUIHealth) {
        return (
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">{t('systemHealth.title')}</h2>
                </div>
                <div className="loading">{t('systemHealth.waitingForData')}</div>
            </div>
        );
    }

    const { health, teams, models, openWebUIHealth, activeUsers, activeUserNames, litellmPing, openWebUIPing, groups, unassignedUsersCount, recentlyProvisioned, unassignedUsersList } = data;

    const unassignedCount = unassignedUsersCount ?? 0;
    const recentEntries = Array.isArray(recentlyProvisioned) ? recentlyProvisioned : [];
    const unassignedEntries = Array.isArray(unassignedUsersList) ? unassignedUsersList : [];

    const groupNames = new Set((groups || []).map(g => g.name.trim().toLowerCase()));
    const visibleTeams = groupNames.size > 0
        ? teams.filter(t => t.team_alias && groupNames.has(t.team_alias.trim().toLowerCase()))
        : teams;

    // All LiteLLM teams with a budget — used for budget/blocked cards so provider-only
    // teams (no OpenWebUI group) still surface when they go over budget.
    const budgetTeams = teams.filter(t => t.max_budget != null && t.max_budget > 0);

    const blockedTeams = budgetTeams.filter(t => t.blocked === true);
    const blockedTeamsCount = blockedTeams.length;

    const blockedKeys = budgetTeams.reduce((acc, team) => {
        const teamBlockedKeys = (team.keys || []).filter(k => k.blocked === true);
        return acc.concat(teamBlockedKeys.map(k => ({ ...k, team_alias: team.team_alias })));
    }, []);
    const blockedKeysCount = blockedKeys.length;

    const totalUsers = visibleTeams.reduce((total, team) => {
        if (!team?.team_alias) return total;
        const matchingGroup = groups?.find(
            g => g.name.trim().toLowerCase() === team.team_alias.trim().toLowerCase()
        );
        return matchingGroup && Array.isArray(matchingGroup.user_ids)
            ? total + matchingGroup.user_ids.length
            : total;
    }, 0);

    const visibleModels = models.filter(model => {
        const modelName = model.id || '';
        return visibleTeams.some(team => {
            const teamPrefix = team.team_alias?.split(' ')[0]?.toUpperCase();
            return modelName.startsWith(`${teamPrefix}.`);
        });
    });

    const teamBudgetIssues = budgetTeams.filter(team => isTeamBudgetAlert(team) && !acknowledgedBlocks[team.team_id]);
    const globalThresholds = getGlobalThresholds();
    const hasTeamBudgetIssues = teamBudgetIssues.length > 0;
    const unacknowledgedBlockedCount = blockedTeams.filter(t => !acknowledgedBlocks[t.team_id]).length;
    const isOpenWebUIHealthy = openWebUIHealth === 'healthy';

    const nonRouterModels = visibleModels.filter(m => !(m.id || m.name || '').includes('auto-router'));
    const unhealthyUserModels = [];

    if (health?.unhealthy_endpoints) {
        nonRouterModels.forEach(model => {
            const modelName = model.id || model.name || '';
            const unhealthyEndpoint = health.unhealthy_endpoints.find(ep =>
                (ep.model || '').includes(modelName.replace(/^(PLUS\.|CNX\.|BKK\.)/, ''))
            );
            if (unhealthyEndpoint) {
                unhealthyUserModels.push({ modelName, error: unhealthyEndpoint.error });
            }
        });
    }

    const totalModelsForHealth = nonRouterModels.length;
    const unhealthyModelsCount = unhealthyUserModels.length;
    const healthyModelsCount = totalModelsForHealth - unhealthyModelsCount;
    const unhealthyRatio = totalModelsForHealth > 0 ? unhealthyModelsCount / totalModelsForHealth : 0;

    const litellmSeverity = !isLiteLLMReachable ? 'critical'
        : unhealthyModelsCount === 0 ? 'ok'
        : unhealthyRatio <= globalThresholds.healthCritical ? 'warning'
        : 'critical';

    const openWebuiSeverity = !isOpenWebUIReachable ? 'critical'
        : isOpenWebUIHealthy ? 'ok'
        : 'critical';

    // Detail panel content builders
    const teamBudgetDetail = hasTeamBudgetIssues ? (
        teamBudgetIssues.map((team, i) => {
            const pct = Math.round(((team.spend || 0) / (team.max_budget || 1)) * 100);
            return (
                <div key={i} className="stat-card-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <span className="stat-card-detail-name">{team.team_alias}</span>
                    <span className="stat-card-detail-value">{pct}% &mdash; ${(team.spend || 0).toFixed(2)} / ${(team.max_budget || 0).toFixed(2)}</span>
                </div>
            );
        })
    ) : (
        <div className="stat-card-detail-empty">{t('systemHealth.allBudgetsOk')}</div>
    );

    const healthyModelsDetail = (() => {
        const healthy = nonRouterModels.filter(m => !unhealthyUserModels.some(u => u.modelName === (m.id || m.name || '')));
        return healthy.length === 0 ? (
            <div className="stat-card-detail-empty">{t('systemHealth.noHealthyModels')}</div>
        ) : (
            healthy.map((m, i) => (
                <div key={i} className="stat-card-detail-row">
                    <span className="stat-card-detail-name">{m.id || m.name}</span>
                </div>
            ))
        );
    })();

    const unhealthyModelsDetail = unhealthyModelsCount === 0 ? (
        <div className="stat-card-detail-empty">{t('systemHealth.allModelsHealthy')}</div>
    ) : (
        unhealthyUserModels.map((m, i) => {
            const err = m.error ? m.error.split('\n')[0].replace('litellm.', '') : t('systemHealth.unknownError');
            return (
                <div key={i} className="stat-card-detail-row">
                    <span className="stat-card-detail-name">{m.modelName}</span>
                    <span className="stat-card-detail-value stat-card-detail-error">{err}</span>
                </div>
            );
        })
    );

    const blockedTeamsDetail = blockedTeamsCount === 0 ? (
        <div className="stat-card-detail-empty">{t('systemHealth.noTeamsBlocked')}</div>
    ) : (
        blockedTeams.map((team, i) => {
            const ack = acknowledgedBlocks[team.team_id];
            return (
                <div key={i} className="stat-card-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                        <span className="stat-card-detail-name">{team.team_alias || team.team_id}</span>
                        {ack ? (
                            <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'rgba(107,114,128,0.12)', color: 'var(--muted)', border: '1px solid rgba(107,114,128,0.3)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>Acknowledged</span>
                        ) : null}
                    </div>
                    {ack ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>by {ack.acknowledgedBy}</span>
                            {isAdmin && (
                                <button
                                    onClick={e => handleUnacknowledge(e, team.team_id)}
                                    style={{ fontSize: '0.68rem', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    ) : isAdmin ? (
                        <button
                            onClick={e => handleAcknowledge(e, team)}
                            style={{ fontSize: '0.68rem', color: 'var(--muted)', background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', padding: '1px 6px' }}
                        >
                            Acknowledge
                        </button>
                    ) : null}
                </div>
            );
        })
    );

    const mcpActiveCount = mcpServers.filter(s => s.config?.enable !== false).length;

    const mcpServersDetail = mcpServers.length === 0 ? (
        <div className="stat-card-detail-empty">No MCP servers configured</div>
    ) : (
        mcpServers.map((s, i) => {
            const enabled = s.config?.enable !== false;
            return (
                <div key={i} className="stat-card-detail-row" style={enabled ? {} : { opacity: 0.45 }}>
                    <span className="stat-card-detail-name">{s.info?.name || s.info?.id || 'Unknown'}</span>
                    <span className="stat-card-detail-value">{enabled ? '' : 'disabled'}</span>
                </div>
            );
        })
    );

    const owuiToolsDetail = owuiTools.length === 0 ? (
        <div className="stat-card-detail-empty">No tools installed</div>
    ) : (
        owuiTools.map((tool, i) => (
            <div key={i} className="stat-card-detail-row">
                <span className="stat-card-detail-name">{tool.name || tool.id || 'Unknown'}</span>
                <span className="stat-card-detail-value">{tool.user?.name || ''}</span>
            </div>
        ))
    );

    const owuiSkillsDetail = owuiSkills.length === 0 ? (
        <div className="stat-card-detail-empty">No skills enabled</div>
    ) : (
        owuiSkills.map((skill, i) => (
            <div key={i} className="stat-card-detail-row">
                <span className="stat-card-detail-name">{skill.name || skill.id || 'Unknown'}</span>
                <span className="stat-card-detail-value">{skill.user?.name || ''}</span>
            </div>
        ))
    );

    const blockedKeysDetail = blockedKeysCount === 0 ? (
        <div className="stat-card-detail-empty">No blocked API keys</div>
    ) : (
        blockedKeys.map((key, i) => {
            const name = key.key_alias || key.key_name || (key.token?.substring(0, 8) + '...') || 'Unknown';
            return (
                <div key={i} className="stat-card-detail-row">
                    <span className="stat-card-detail-name">{name}</span>
                    <span className="stat-card-detail-value">{key.team_alias}</span>
                </div>
            );
        })
    );

    const activeUsersDetail = (!activeUserNames || activeUserNames.length === 0) ? (
        <div className="stat-card-detail-empty">No active sessions</div>
    ) : (
        activeUserNames.map((name, i) => (
            <div key={i} className="stat-card-detail-row">
                <span className="stat-card-detail-name">{name}</span>
            </div>
        ))
    );

    const groupsDetail = (!groups || groups.length === 0) ? (
        <div className="stat-card-detail-empty">No groups configured</div>
    ) : (
        groups.map((g, i) => (
            <div key={i} className="stat-card-detail-row">
                <span className="stat-card-detail-name">{g.name}</span>
                <span className="stat-card-detail-value">{(g.user_ids || []).length} users</span>
            </div>
        ))
    );

    const litellmDetail = (
        <div className="stat-card-detail-row">
            <span className="stat-card-detail-name">
                {!isLiteLLMReachable
                    ? (litellmPing?.error || t('systemHealth.connectionFailed'))
                    : t('systemHealth.litellmHealthChecks')}
            </span>
            {litellmPing?.latency > 0 && isLiteLLMReachable && (
                <span className="stat-card-detail-value">{litellmPing.latency}ms</span>
            )}
        </div>
    );

    const openwebuiDetail = (
        <div className="stat-card-detail-row">
            <span className="stat-card-detail-name">
                {!isOpenWebUIReachable
                    ? (openWebUIPing?.error || t('systemHealth.connectionFailed'))
                    : t('systemHealth.openwebuiAvailability')}
            </span>
            {openWebUIPing?.latency > 0 && isOpenWebUIReachable && (
                <span className="stat-card-detail-value">{openWebUIPing.latency}ms</span>
            )}
        </div>
    );

    return (
        <>
        <div className="card">
            <div className="card-header">
                <h2 className="card-title">{t('systemHealth.title')}</h2>
            </div>

            {/* Connectivity alert banner */}
            {(!isLiteLLMReachable || !isOpenWebUIReachable) && (
                <div className="alert-banner">
                    <div>
                        <div className="alert-banner-title">{t('systemHealth.criticalAlert')}</div>
                        {!isLiteLLMReachable && (
                            <div className="alert-banner-body">
                                <strong>Ask Engine:</strong> {litellmPing?.error || t('systemHealth.connectionFailed')}
                                {litellmPing?.status > 0 && ` (HTTP ${litellmPing.status})`}
                            </div>
                        )}
                        {!isOpenWebUIReachable && (
                            <div className="alert-banner-body">
                                <strong>Ask Front End:</strong> {openWebUIPing?.error || t('systemHealth.connectionFailed')}
                                {openWebUIPing?.status > 0 && ` (HTTP ${openWebUIPing.status})`}
                            </div>
                        )}
                        <div className="alert-banner-body" style={{ marginTop: '0.5rem', opacity: 0.8 }}>
                            {t('systemHealth.criticalAlertMessage')}
                        </div>
                    </div>
                </div>
            )}

            {/* Ask Engine section */}
            <div className="section-label">Ask Engine</div>
            <div className="stats-grid stats-grid-4">

                <ExpandableCard id="litellm" expanded={expandedCard === 'litellm'} onToggle={toggleCard}
                    className={`stat-card--${litellmSeverity}`} detail={litellmDetail}>
                    <div className="stat-card-label">Ask Engine</div>
                    <div className="stat-card-value">
                        {!isLiteLLMReachable
                            ? t('systemHealth.unreachable')
                            : unhealthyModelsCount === 0
                                ? t('systemHealth.online')
                                : unhealthyRatio <= globalThresholds.healthCritical
                                    ? t('systemHealth.degraded')
                                    : t('systemHealth.offline')}
                    </div>
                    {litellmPing?.latency > 0 && isLiteLLMReachable && (
                        <div className="stat-card-meta">{litellmPing.latency}ms</div>
                    )}
                </ExpandableCard>

                <ExpandableCard id="teamBudgets" expanded={expandedCard === 'teamBudgets'} onToggle={toggleCard}
                    className={hasTeamBudgetIssues ? 'stat-card--warning' : 'stat-card--ok'} detail={teamBudgetDetail}>
                    <div className="stat-card-label">{t('systemHealth.teamBudgets')}</div>
                    <div className="stat-card-value">
                        {hasTeamBudgetIssues ? t('systemHealth.alertCount', { count: teamBudgetIssues.length }) : t('systemHealth.allOk')}
                    </div>
                </ExpandableCard>

                <ExpandableCard id="healthyModels" expanded={expandedCard === 'healthyModels'} onToggle={toggleCard}
                    className="stat-card--ok" detail={healthyModelsDetail}>
                    <div className="stat-card-label">{t('systemHealth.healthyModels')}</div>
                    <div className="stat-card-value">{health ? healthyModelsCount : totalModelsForHealth}</div>
                    {lastChecked && (() => {
                        const isToday = lastChecked.toDateString() === new Date().toDateString();
                        const label = isToday
                            ? lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : lastChecked.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return <div className="stat-card-meta">Checked {label}</div>;
                    })()}
                </ExpandableCard>

                <ExpandableCard id="unhealthyModels" expanded={expandedCard === 'unhealthyModels'} onToggle={toggleCard}
                    className={unhealthyModelsCount > 0 ? 'stat-card--critical' : 'stat-card--ok'} detail={unhealthyModelsDetail}>
                    <div className="stat-card-label">{t('systemHealth.unhealthyModels')}</div>
                    <div className="stat-card-value">{health ? unhealthyModelsCount : '—'}</div>
                    <button className="stat-card-action" onClick={handleCheckModels} disabled={checkingModels}>
                        {checkingModels ? 'Checking...' : health ? 'Recheck' : 'Check Models'}
                    </button>
                </ExpandableCard>

                <ExpandableCard id="blockedTeams" expanded={expandedCard === 'blockedTeams'} onToggle={toggleCard}
                    className={unacknowledgedBlockedCount > 0 ? 'stat-card--critical' : blockedTeamsCount > 0 ? 'stat-card--warning' : 'stat-card--ok'} detail={blockedTeamsDetail}>
                    <div className="stat-card-label">{t('systemHealth.blockedTeams')}</div>
                    <div className="stat-card-value">{blockedTeamsCount}</div>
                </ExpandableCard>

                <ExpandableCard id="blockedKeys" expanded={expandedCard === 'blockedKeys'} onToggle={toggleCard}
                    className={blockedKeysCount > 0 ? 'stat-card--critical' : 'stat-card--ok'} detail={blockedKeysDetail}>
                    <div className="stat-card-label">Blocked Keys</div>
                    <div className="stat-card-value">{blockedKeysCount}</div>
                </ExpandableCard>

                <ExpandableCard id="mcpServers" expanded={expandedCard === 'mcpServers'} onToggle={toggleCard}
                    className="stat-card--ok" detail={mcpServersDetail}>
                    <div className="stat-card-label">MCP Servers</div>
                    <div className="stat-card-value">{mcpActiveCount}</div>
                    {mcpServers.length > 0 && (
                        <div className="stat-card-meta">
                            {mcpActiveCount === mcpServers.length
                                ? `${mcpServers.length} configured`
                                : `${mcpActiveCount} active of ${mcpServers.length}`}
                        </div>
                    )}
                </ExpandableCard>

            </div>

            {/* Unhealthy model detail panel (always-visible when there are issues) */}
            {unhealthyUserModels.length > 0 && (
                <div className="unhealthy-panel">
                    <div className="unhealthy-panel-title">
                        {t('systemHealth.modelsUnhealthyCount', { count: unhealthyUserModels.length })}
                    </div>
                    {unhealthyUserModels.map((model, idx) => (
                        <div key={idx} className="unhealthy-panel-row">
                            <div className="unhealthy-panel-name">{model.modelName}</div>
                            <div className="unhealthy-panel-error">
                                {model.error ? model.error.split('\n')[0].replace('litellm.', '') : t('systemHealth.unknownError')}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Ask Front End section */}
            <div className="section-label">Ask Front End</div>

            {/* Row 1 — Service & Users */}
            <div className="stats-grid stats-grid-4">

                <ExpandableCard id="openwebui" expanded={expandedCard === 'openwebui'} onToggle={toggleCard}
                    className={`stat-card--${openWebuiSeverity}`} detail={openwebuiDetail}>
                    <div className="stat-card-label">Ask Front End</div>
                    <div className="stat-card-value">
                        {!isOpenWebUIReachable
                            ? t('systemHealth.unreachable')
                            : isOpenWebUIHealthy
                                ? t('systemHealth.online')
                                : t('systemHealth.offline')}
                    </div>
                    {openWebUIPing?.latency > 0 && isOpenWebUIReachable && (
                        <div className="stat-card-meta">{openWebUIPing.latency}ms</div>
                    )}
                </ExpandableCard>

                <div className="stat-card stat-card--ok">
                    <div className="stat-card-label">{t('systemHealth.totalUsers')}</div>
                    <div className="stat-card-value">{totalUsers}</div>
                </div>

                <ExpandableCard id="activeUsers" expanded={expandedCard === 'activeUsers'} onToggle={toggleCard}
                    className="stat-card--ok" detail={activeUsersDetail}>
                    <div className="stat-card-label">{t('systemHealth.activeUsers')}</div>
                    <div className="stat-card-value">{activeUsers}</div>
                    {activeUsers > 0 && <div className="stat-card-meta">active sessions</div>}
                </ExpandableCard>

                <div
                    className={`stat-card stat-card--expandable ${unassignedCount > 0 ? 'stat-card--warning' : 'stat-card--ok'}`}
                    onClick={() => setShowUnassignedModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowUnassignedModal(true)}
                >
                    <div className="stat-card-expand-chevron">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round">
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </div>
                    <div className="stat-card-label">{t('systemHealth.unassignedUsers')}</div>
                    <div className="stat-card-value">{unassignedCount}</div>
                </div>

            </div>

            {/* Row 2 — Content & Organisation */}
            <div className="stats-grid stats-grid-4">

                {Array.isArray(groups) && (
                    <ExpandableCard id="groups" expanded={expandedCard === 'groups'} onToggle={toggleCard}
                        className="stat-card--ok" detail={groupsDetail}>
                        <div className="stat-card-label">Groups</div>
                        <div className="stat-card-value">{groups.length}</div>
                    </ExpandableCard>
                )}

                <ExpandableCard id="owuiTools" expanded={expandedCard === 'owuiTools'} onToggle={toggleCard}
                    className="stat-card--ok" detail={owuiToolsDetail}>
                    <div className="stat-card-label">Tools</div>
                    <div className="stat-card-value">{owuiTools.length}</div>
                    {owuiTools.length > 0 && (
                        <div className="stat-card-meta">{owuiTools.length === 1 ? '1 installed' : `${owuiTools.length} installed`}</div>
                    )}
                </ExpandableCard>

                <ExpandableCard id="owuiSkills" expanded={expandedCard === 'owuiSkills'} onToggle={toggleCard}
                    className="stat-card--ok" detail={owuiSkillsDetail}>
                    <div className="stat-card-label">Skills</div>
                    <div className="stat-card-value">{owuiSkills.length}</div>
                    {owuiSkills.length > 0 && (
                        <div className="stat-card-meta">{owuiSkills.length === 1 ? '1 enabled' : `${owuiSkills.length} enabled`}</div>
                    )}
                </ExpandableCard>

                {isAdmin && (
                    <div
                        className="stat-card stat-card--expandable stat-card--ok"
                        onClick={() => setShowRecentModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowRecentModal(true)}
                    >
                        <div className="stat-card-expand-chevron">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </div>
                        <div className="stat-card-label">{t('systemHealth.recentlyAssigned')}</div>
                        <div className="stat-card-value">{recentEntries.length}</div>
                        <div className="stat-card-meta">{t('systemHealth.lastHour')}</div>
                    </div>
                )}

            </div>
        </div>

        {showUnassignedModal && (
            <div className="modal-overlay" onClick={() => setShowUnassignedModal(false)}>
                <div className="modal-box" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <span className="modal-title">{t('systemHealth.unassignedUsers')}</span>
                        <button className="modal-close" onClick={() => setShowUnassignedModal(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                        {unassignedEntries.length === 0 ? (
                            <p className="modal-empty">{t('systemHealth.noUnassignedUsers')}</p>
                        ) : (
                            <table className="recent-provision-table">
                                <thead>
                                    <tr>
                                        <th>{t('systemHealth.recentColEmail')}</th>
                                        <th>{t('systemHealth.recentColName')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unassignedEntries.map((e, i) => (
                                        <tr key={i}>
                                            <td className="recent-provision-email">{e.email}</td>
                                            <td>{e.name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {unassignedEntries.length > 0 && onProvision && (
                            <div style={{ marginTop: '16px', textAlign: 'right' }}>
                                <button className="btn btn-primary" onClick={() => { setShowUnassignedModal(false); onProvision(); }}>
                                    {t('systemHealth.provisionNow')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {showRecentModal && (
            <div className="modal-overlay" onClick={() => setShowRecentModal(false)}>
                <div className="modal-box" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <span className="modal-title">{t('systemHealth.recentlyAssigned')}</span>
                        <button className="modal-close" onClick={() => setShowRecentModal(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                        {recentEntries.length === 0 ? (
                            <p className="modal-empty">{t('systemHealth.noRecentAssignments')}</p>
                        ) : (
                            <table className="recent-provision-table">
                                <thead>
                                    <tr>
                                        <th>{t('systemHealth.recentColEmail')}</th>
                                        <th>{t('systemHealth.recentColGroup')}</th>
                                        <th>{t('systemHealth.recentColActor')}</th>
                                        <th>{t('systemHealth.recentColTime')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentEntries.map((e, i) => (
                                        <tr key={i}>
                                            <td className="recent-provision-email">{e.email}</td>
                                            <td>{e.group}</td>
                                            <td className="recent-provision-actor">{e.actor === 'SYSTEM' ? 'Auto' : e.actor}</td>
                                            <td className="recent-provision-time">{formatRelativeTime(e.timestamp)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

SystemHealth.propTypes = {
    data: PropTypes.shape({
        health: PropTypes.object,
        teams: PropTypes.array,
        models: PropTypes.array,
        openWebUIHealth: PropTypes.string,
        activeUsers: PropTypes.number,
        activeUserNames: PropTypes.array,
        litellmPing: PropTypes.object,
        openWebUIPing: PropTypes.object,
        groups: PropTypes.array,
        unassignedUsersCount: PropTypes.number,
        recentlyProvisioned: PropTypes.array,
        unassignedUsersList: PropTypes.array,
    }),
    loading: PropTypes.bool,
    loadingStatus: PropTypes.object,
    loadingMessage: PropTypes.string,
    onProvision: PropTypes.func,
    mcpServers: PropTypes.array,
    owuiTools: PropTypes.array,
    owuiSkills: PropTypes.array,
    isAdmin: PropTypes.bool,
    onCheckModels: PropTypes.func,
};

SystemHealth.defaultProps = {
    data: {},
    loading: false,
    loadingStatus: null,
    loadingMessage: '',
};

export default SystemHealth;

